<script setup>
// 小尺寸圖片輪播（活動辦法的獎品卡）：多張淡入淡出自動播，底下一排指示點。
// 與步道詳情的輪播同一套做法——用 Swiper 核心 API 自己掛 DOM，不用 swiper/vue 的元件
// （那組在 Vue 3.5 下會噴「Missing ref owner context」警告）；手勢、指示點、頭尾循環
// 這些邊界情況都交給 Swiper，不自己接 pointer 事件。
// 只有一張圖時不自動播、不畫指示點，手勢一併關掉。
import Swiper from 'swiper'
import { A11y, Autoplay, EffectFade, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/pagination'

const props = defineProps({
  // 已補上網域的圖片網址陣列
  images: { type: Array, default: () => [] },
  alt: { type: String, default: '' },
  // 一頁有好幾張卡同時在播，各自錯開起播時間，才不會整排同一瞬間換圖
  delay: { type: Number, default: 3500 }
})

const FADE_MS = 600

const rootRef = ref(null)
const pagerRef = ref(null)
let swiper = null

const multi = computed(() => props.images.length > 1)

function destroySwiper() {
  if (!swiper) return
  swiper.destroy(true, true)
  swiper = null
}

function initSwiper() {
  destroySwiper()
  if (!rootRef.value || !props.images.length) return

  swiper = new Swiper(rootRef.value, {
    modules: [A11y, Autoplay, EffectFade, Pagination],
    effect: 'fade',
    fadeEffect: { crossFade: true },
    speed: FADE_MS,
    loop: multi.value,
    allowTouchMove: multi.value,
    autoplay: multi.value
      ? { delay: props.delay, disableOnInteraction: false, pauseOnMouseEnter: true }
      : false,
    pagination: multi.value ? { el: pagerRef.value, clickable: true } : false,
    a11y: {
      prevSlideMessage: '上一張圖片',
      nextSlideMessage: '下一張圖片',
      paginationBulletMessage: '看第 {{index}} 張圖片'
    }
  })

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) swiper.autoplay?.stop()
}

onMounted(initSwiper)
onBeforeUnmount(destroySwiper)

// flush: 'post' 等 v-for 把新的 slide 畫完再重建
watch(() => props.images, initSwiper, { flush: 'post' })
</script>

<template>
  <!-- Swiper 初始化前這裡就是第一張圖，SSR 出去的畫面不會跳 -->
  <div ref="rootRef" class="carousel swiper">
    <div class="swiper-wrapper">
      <div v-for="(src, i) in images" :key="`${i}-${src}`" class="swiper-slide">
        <img :src="src" :alt="i === 0 ? alt : ''" draggable="false" />
      </div>
    </div>
    <div v-show="multi" ref="pagerRef" class="swiper-pagination"></div>
  </div>
</template>

<style scoped>
/* 放在 flex / grid 裡時要鎖住最小寬度，否則 Swiper 排出來的 slide 會把父層撐開 */
.carousel {
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  height: 100%;
  overflow: hidden;
  /* 橫向留給輪播的手勢，縱向照常捲頁 */
  touch-action: pan-y;
}

.swiper-slide {
  display: flex;
  align-items: center;
  justify-content: center;
  /* fade 模式下非當前的 slide 也疊在同一格，底色要蓋得住才不會透出上一張 */
  background: #fff;
}

.swiper-slide img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
}

/* 指示點：Swiper 產生的節點吃不到 scoped 屬性，一律用 :deep() 收。
   浮在圖片上，不另外佔高度——否則多圖的卡片圖會比單圖的小一圈。
   底下墊一顆半透明白膠囊，壓在深色或白底的獎品圖上都看得到點 */
.carousel :deep(.swiper-pagination) {
  position: absolute;
  left: 50%;
  bottom: 8px;
  z-index: 2;
  display: flex;
  width: auto;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.8);
  transform: translateX(-50%);
  --swiper-pagination-bullet-horizontal-gap: 0;
}

.carousel :deep(.swiper-pagination-bullet) {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.2);
  opacity: 1;
  cursor: pointer;
  transition:
    width 0.35s cubic-bezier(0.22, 0.61, 0.36, 1),
    background-color 0.35s;
}

.carousel :deep(.swiper-pagination-bullet-active) {
  width: 20px;
  background: var(--site-orange);
}
</style>
